import { forwardRef } from 'react';
import cx from 'classnames';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { postfixTestId } from '../../util/testId';
import { useDensity } from '../Density';
import { Label } from '../Label';
import { Text } from '../Text';
import cn from './withLabels.css.json';
import './withLabels.css';

/**
 * internal props of Labels
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const isFieldWidthAPercentage = fieldWidth => {
  if (typeof fieldWidth !== 'string') return false;
  return fieldWidth.includes('%');
};
const Labels = props => {
  const dt = useDesignTokens();
  const {
    density
  } = useDensity();
  const fieldWidthAsPercentage = isFieldWidthAPercentage(props.fieldWidth);
  const fauxLabelStyling = props => ({
    fg: props.disabled ? dt.tokens['color-action-text-standard-disabled'] : dt.tokens['color-label-text']
  });
  const style = renderInlineCssVars({
    'field-label-width': props.fieldLabelWidth,
    'field-label-min-width': props.fieldLabelMinWidth,
    'field-width': props.fieldWidth,
    'field-min-width': props.fieldMinWidth,
    'field-suffix-min-width': props.suffix && (props.fieldSuffixMinWidth || '3rem'),
    // the line-height of the input is a fixed value 'normal', and the line-height of the label can be changed. Normally it's 1.4, and in zh-CN-xx it will be 1.5, which will cause the label and input to be misaligned (NOVACOMMONS-24421).
    'spacing-xs': Number(dt.tokens['font-space-line-height']) > 1.4 ? dt.tokens['space-block-xxs'] : dt.tokens['space-block-xs'],
    'spacing-s': dt.tokens['space-base-s'],
    'spacing-m': dt.tokens['space-base-m'],
    'spacing-end': density === 'high' ? dt.tokens['space-base-s'] : dt.tokens['space-base-m'],
    ...(props.labelSpacing?.leftAligned ? {
      'horizontal-spacing-override': dt.spacing.core[props.labelSpacing.leftAligned]
    } : {}),
    ...(props.labelSpacing?.topAligned ? {
      'vertical-spacing-override': dt.spacing.core[props.labelSpacing.topAligned]
    } : {}),
    'font-size-s': dt.tokens['font-size-body-text-s']
  }, 'with-labels');
  const densityStyles = density === 'high' ? {
    '--label-font-size': dt.tokens['font-size-body-text-s']
  } : undefined;
  return /*#__PURE__*/_jsxs("div", {
    className: cx(props.className, cn.withLabels, fieldWidthAsPercentage && cn.fieldWidthAsPercentage, props.labelPosition === 'left' ? cn.labelLeft : cn.labelTop),
    style: {
      ...style,
      ...props.style
    },
    "data-testid": props.testId,
    children: [props.label && /*#__PURE__*/_jsx("div", {
      className: cn.label,
      children: /*#__PURE__*/_jsx(Label, {
        containerRef: props.labelContainerRef,
        id: props.labelId,
        testId: postfixTestId(props.testId, 'label'),
        className: props.labelClassName,
        style: {
          ...densityStyles,
          ...props.labelStyle
        },
        disabled: props.disabled,
        required: !props.suppressRequiredIndicator && props.required,
        htmlFor: !props.suppressHtmlFor ? props.inputId : undefined,
        wrapText: props.wrapLabel,
        helpPopup: props.labelHelp,
        highlightableConfig: props.labelHighlightableConfig,
        theme: props.labelTheme,
        children: props.label
      })
    }), props.children && /*#__PURE__*/_jsx("div", {
      className: cn.input,
      children: props.children
    }), props.suffix && /*#__PURE__*/_jsx("div", {
      className: cn.suffix,
      children: /*#__PURE__*/_jsx(Text, {
        id: props.suffixId,
        testId: postfixTestId(props.testId, 'suffix'),
        disabled: props.disabled,
        wrapText: false,
        tokens: fauxLabelStyling,
        ...(density === 'high' && {
          style: {
            '--text-font-size-m': dt.tokens['font-size-body-text-s']
          }
        }),
        children: props.suffix
      })
    }), props.description && /*#__PURE__*/_jsx("div", {
      className: cn.description,
      children: /*#__PURE__*/_jsx(Text, {
        id: props.descriptionId,
        testId: postfixTestId(props.testId, 'description'),
        className: cn.text,
        disabled: props.disabled,
        wrapText: props.wrapDescription,
        tokens: fauxLabelStyling,
        children: props.description
      })
    })]
  });
};
const useHtmlId = createUseHtmlId('withLabels');

/**
 * A HOC to enable components to have label, description, and suffix.
 * It accepts a Component and returns a new Component which would render a grid containing the label, Component, suffix and description.
 * data-with-labels prop is passed down for components to check if it is used under withLabels.
 *
 * @remarks
 * Text and BaseText are not allowed to be used with withLabels, this may cause the accessibility problem. Please use readonly {@link Input}, {@link Textarea}, {@link Select}, etc. instead of Text or BaseText.
 *
 * @param Comp the Component to be labeled and described.
 * @return A higher order component that supports prop label, description, suffix and so on.
 * @example
 * ```tsx
 * import { withLabels } from '@sas/nova-commons/components/withLabels';
 * import { Input } from '@sas/nova-commons/components/Input';
 *
 * const InputWithLabels = withLabels(Input);
 *
 * export const MyComp = () => {
 *   return (
 *     <InputWithLabels label="label" description="description" value="value" onChange={() => {}} />
 *   )
 * }
 * ```
 */
export function withLabels(Comp) {
  const displayName = `${Comp.name}WithLabels`;
  const LabelsWrapper = /*#__PURE__*/forwardRef(function LabelsWrapper(props, ref) {
    const [id, autoLabelId, suffixId, descriptionId] = useHtmlId(4);
    const inputId = props.id || id;
    const labelProps = props;
    const {
      className,
      style,
      label,
      labelId: labelIdProp,
      labelClassName,
      labelStyle,
      labelPosition,
      labelHelp,
      labelContainerRef,
      labelHighlightableConfig,
      labelTheme,
      suffix,
      description,
      wrapLabel,
      wrapDescription,
      suppressHtmlFor,
      suppressRequiredIndicator,
      fieldLabelWidth,
      fieldLabelMinWidth,
      fieldMinWidth,
      fieldWidth,
      fieldSuffixMinWidth,
      labelSpacing,
      labelingAttr,
      inputStyle,
      inputClassName,
      ariaLabel: originalAriaLabel,
      ariaLabelledBy: originalAriaLabelledBy,
      ariaDescribedBy: originalAriaDescribedBy,
      ...compProps
    } = props; // some components are going to set {...props} to a DOM so we should give the exact comp props (get rid of WithLabelProps except required and testId)
    const fieldWidthAsPercentage = isFieldWidthAPercentage(fieldWidth);
    const labelId = labelIdProp ?? autoLabelId;
    /*
     * aria-label would override the htmlFor but be overridden by aria-labelledy
     * htmlFor association supports a focus feature natively that the focus would move to the component when click the label
     * Some components have an inner aria-label. So withLabels need to prepend the label to the aria-label
     * Some components also allow customized aria-labelledby. So withLabels need to prepend the label id to the aria-labelledby
     */
    const ariaLabel = labelingAttr === 'aria-label' || labelingAttr === undefined && !!originalAriaLabel ? [label, originalAriaLabel].filter(label => !!label).join(', ') : undefined;
    const ariaLabelledBy = labelingAttr === 'aria-labelledby' || labelingAttr === undefined && !ariaLabel ? [labelId].concat(originalAriaLabelledBy ?? []).join(' ') : undefined;
    let ariaDescribedBy = [];
    if (suffix) ariaDescribedBy.push(suffixId);
    if (description) ariaDescribedBy.push(descriptionId);
    if (originalAriaDescribedBy) ariaDescribedBy = ariaDescribedBy.concat(originalAriaDescribedBy);
    const ariaProps = {};
    if (ariaLabel !== undefined) ariaProps.ariaLabel = ariaLabel;
    if (ariaLabelledBy !== undefined) ariaProps.ariaLabelledBy = ariaLabelledBy;
    if (ariaDescribedBy.length > 0) ariaProps.ariaDescribedBy = [ariaDescribedBy.join(' ')];
    // there are two types, string or string[], used for ariaDescribedBy in different components,
    // if the string is the type of ariaDescribedBy and the ['abc', 'def'] is passed in, it will be converted to "abc,def", it is incorrect.
    // so concatenate all of elemens and construct a new array with only one element.

    return /*#__PURE__*/_jsx(Labels, {
      className: displayName + (className ? ` ${className}` : ''),
      style: style,
      ...labelProps,
      suppressRequiredIndicator: suppressRequiredIndicator || props.readOnly || props.editable === false,
      inputId: labelingAttr === 'htmlFor' || labelingAttr === undefined && !ariaLabel ? inputId : undefined,
      labelId: labelId,
      suffixId: suffixId,
      descriptionId: descriptionId,
      testId: postfixTestId(props.testId, 'withLabels'),
      children: /*#__PURE__*/_jsx(Comp, {
        ...compProps,
        id: inputId,
        ref: ref,
        className: inputClassName
        /**
         * data-* attributes are automatically forwarded to components and are safely rendered to the DOM.
         * This allows us to pass this prop freely to any component used by withLabels and components that
         * require this feature can implement the API independently as needed.
         */,
        "data-with-labels": true,
        style: {
          maxWidth: '100%',
          ...inputStyle,
          width: fieldWidthAsPercentage ? '100%' : fieldWidth,
          minWidth: fieldMinWidth
        },
        ...ariaProps
      })
    });
    // Not sure why but the type check does not work for <Comp />. It could accept ref and aria properties that it may not support.
  });

  LabelsWrapper.displayName = displayName;
  return LabelsWrapper;
}
//# sourceMappingURL=withLabels.js.map